/**
 * Wish List store (wish-list-screen).
 *
 * A trainer's Wish List is an ordered list of up to seven Pokemon they are
 * "chasing" — the exact copies they want friends to help them find. Each slot
 * is identified by the same `(variant, dexNumber, formId?)` triple the Pokedex
 * uses (see `dexStateStore.keyFor`), so a wished "XXL Pikachu" is distinct from
 * a wished "Shiny Pikachu": only the cell in the XXL dex lights up green.
 *
 * Storage model (mirrors the local-first half of dexStateStore):
 *   - localStorage (`nexus.wishlist.v1`) is the device-local source of truth so
 *     the long-press "Add to wish list" affordance works with no server
 *     roundtrip and survives a reload.
 *   - `WISHLIST_EVENT` fires on every mutation so every surface (the Pokedex
 *     grid, the sub-dex form grid, the detail page, the Wish List screen) can
 *     re-render without polling.
 *   - `WishlistSyncer` layers the optional server sync on top: it hydrates from
 *     `GET /api/wishlist` on sign-in and pushes local changes with a debounced
 *     `PUT /api/wishlist`, so friends can read the list on the Compare page. A
 *     missing endpoint is a documented gap, not a hard error — the local list
 *     keeps working either way.
 *
 * No hardcoded hex colours, no emojis — the UI layers read theme tokens.
 */

import { useSyncExternalStore } from "react";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

export const WISHLIST_EVENT = "nexus:wishlist-changed";
const STORAGE_KEY = "nexus.wishlist.v1";

/** Maximum number of Pokemon a trainer can pin to their Wish List. */
export const MAX_WISHLIST = 7;

/**
 * One wished copy. `variant` is the dex variant (Shiny / XXL / Lucky / ...),
 * `dexNumber` the national dex id, and `formId` an optional PokeAPI form slug
 * (e.g. `vivillon-meadow`) when the wish is for a specific form rather than the
 * base species. `addedAt` preserves insertion order for the seven slots.
 */
export interface WishlistEntry {
  variant: PokedexVariant;
  dexNumber: number;
  formId?: string;
  addedAt: number;
}

/**
 * Canonical identity key for a wished copy. Mirrors dexStateStore's `keyFor`
 * shape so the green "wished" indicator on a dex cell can be looked up with the
 * same `(variant, dexNumber, formId?)` the cell already knows.
 */
export function wishlistKey(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): string {
  return formId
    ? `${variant}:${dexNumber}:${formId}`
    : `${variant}:${dexNumber}`;
}

/** Identity key for a stored entry. */
export function entryKey(entry: WishlistEntry): string {
  return wishlistKey(entry.variant, entry.dexNumber, entry.formId);
}

// Stable empty snapshot for the server (SSR/test) render of useSyncExternalStore
// and as the reset value — must be a constant reference so React sees no change.
const EMPTY: readonly WishlistEntry[] = Object.freeze([]);

// Module cache of the parsed list. useSyncExternalStore requires getSnapshot to
// return a STABLE reference between renders unless the data actually changed, so
// we cache the parsed array and only rebuild it when a mutation invalidates it.
let cache: WishlistEntry[] | null = null;

function sanitize(raw: unknown): WishlistEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: WishlistEntry[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const variant = rec.variant;
    const dexNumber = rec.dexNumber;
    if (typeof variant !== "string") continue;
    if (typeof dexNumber !== "number" || !Number.isFinite(dexNumber) || dexNumber < 1) continue;
    const formId = typeof rec.formId === "string" && rec.formId ? rec.formId : undefined;
    const key = wishlistKey(variant as PokedexVariant, dexNumber, formId);
    if (seen.has(key)) continue; // de-dupe by identity
    seen.add(key);
    out.push({
      variant: variant as PokedexVariant,
      dexNumber,
      ...(formId ? { formId } : {}),
      addedAt:
        typeof rec.addedAt === "number" && Number.isFinite(rec.addedAt)
          ? rec.addedAt
          : Date.now(),
    });
    if (out.length >= MAX_WISHLIST) break; // hard cap
  }
  return out;
}

function readRaw(): WishlistEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return sanitize(JSON.parse(raw));
  } catch {
    return [];
  }
}

/** The current Wish List (ordered, de-duped, capped at seven). */
export function getWishlist(): WishlistEntry[] {
  if (cache) return cache;
  cache = readRaw();
  return cache;
}

function emitChange(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(WISHLIST_EVENT));
  } catch {
    /* swallow */
  }
}

/**
 * Persist and broadcast. Returns true when the write landed. A blocked/quota
 * localStorage (iOS WKWebView private mode) degrades to in-memory only: the
 * cache still updates so the current session stays consistent.
 */
function commit(list: WishlistEntry[]): boolean {
  const next = list.slice(0, MAX_WISHLIST);
  cache = next;
  let ok = false;
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      ok = true;
    } catch {
      /* quota / blocked — keep the in-memory cache */
    }
  }
  emitChange();
  return ok;
}

// ── Reads ─────────────────────────────────────────────────────────────────

/** Whether a specific `(variant, dexNumber, formId?)` copy is on the list. */
export function isWished(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): boolean {
  const key = wishlistKey(variant, dexNumber, formId);
  return getWishlist().some((e) => entryKey(e) === key);
}

/** Build a Set of identity keys for O(1) per-cell lookups in a grid. */
export function wishlistKeySet(list?: WishlistEntry[]): Set<string> {
  return new Set((list ?? getWishlist()).map(entryKey));
}

// ── Writes ──────────────────────────────────────────────────────────────────

export type AddResult =
  | { ok: true; already: boolean }
  | { ok: false; reason: "full" };

/**
 * Add a copy to the Wish List. Idempotent: adding a copy that is already on the
 * list is a success (`already: true`) and never duplicates it. Fails only when
 * the list is full AND the copy is not already present.
 */
export function addToWishlist(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): AddResult {
  const list = getWishlist();
  const key = wishlistKey(variant, dexNumber, formId);
  if (list.some((e) => entryKey(e) === key)) return { ok: true, already: true };
  if (list.length >= MAX_WISHLIST) return { ok: false, reason: "full" };
  const entry: WishlistEntry = {
    variant,
    dexNumber,
    ...(formId ? { formId } : {}),
    addedAt: Date.now(),
  };
  commit([...list, entry]);
  return { ok: true, already: false };
}

/** Remove a copy from the Wish List. Returns true when a row was removed. */
export function removeFromWishlist(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): boolean {
  const list = getWishlist();
  const key = wishlistKey(variant, dexNumber, formId);
  const next = list.filter((e) => entryKey(e) !== key);
  if (next.length === list.length) return false;
  commit(next);
  return true;
}

/**
 * Toggle a copy on/off the Wish List. Returns the resulting state plus, when a
 * long-press add was rejected because the list is full, `reason: "full"` so the
 * caller can surface a toast.
 */
export function toggleWishlist(
  variant: PokedexVariant,
  dexNumber: number,
  formId?: string | null,
): { wished: boolean; reason?: "full" } {
  if (isWished(variant, dexNumber, formId)) {
    removeFromWishlist(variant, dexNumber, formId);
    return { wished: false };
  }
  const res = addToWishlist(variant, dexNumber, formId);
  if (!res.ok) return { wished: false, reason: "full" };
  return { wished: true };
}

/**
 * Replace the whole list (used by the Wish List screen's slot edits and by the
 * syncer's server hydrate). Sanitised, de-duped and capped to seven.
 */
export function setWishlist(entries: WishlistEntry[]): void {
  commit(sanitize(entries));
}

/** Clear the entire Wish List. */
export function clearWishlist(): void {
  commit([]);
}

// ── React binding ─────────────────────────────────────────────────────────

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(WISHLIST_EVENT, onChange);
  return () => window.removeEventListener(WISHLIST_EVENT, onChange);
}

/** Reactively track the Wish List. Re-renders on every mutation. */
export function useWishlist(): WishlistEntry[] {
  return useSyncExternalStore(subscribe, getWishlist, () => EMPTY as WishlistEntry[]);
}

